import React, { useEffect, useState } from 'react';

/**
 * StandardLoadingScreen, strict 1:1 port of
 * `/tmp/bp-ios/frontend/src/components/StandardLoadingScreen.tsx`.
 *
 * ALWAYS renders full-screen (no inline mode, iOS doesn't have one).
 * Wrap the entire page output with an early-return when the page is in
 * a loading state.
 *
 *  - Free  : static `#1a1a2e → #16213e → #0f3460` linear gradient
 *  - Premium: drifting `#00bfff → #40e0d0 → #9370db → #ba55d3 →
 *             #ff0080 → #ff1493` gradient (4s loop, translate
 *             -SCREEN_WIDTH*3, -SCREEN_HEIGHT*0.3)
 *  - Spinner: 70px Siri-style SVG arc, 1s linear rotation, 75% arc
 *  - Text   : single static `message` OR rotating `messages` array
 *             (2500ms hold + 300ms fade)
 *  - Footer : "Powered by BuildPilot Intelligence", premium only, at
 *             the very bottom of the screen
 */

const DEFAULT_MESSAGES = [
  'Processing your request...',
  'Almost there...',
  'Just a moment...',
];

export const SiriStyleSpinner = ({ size = 70 }) => {
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const dashLength = circumference * 0.75;

  return (
    <span
      className="bp-std-loading-spinner"
      style={{ width: size, height: size }}
      aria-label="Loading"
      role="status"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id="bp-std-siri-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#00bfff" stopOpacity="1" />
            <stop offset="20%"  stopColor="#40e0d0" stopOpacity="0.95" />
            <stop offset="40%"  stopColor="#9370db" stopOpacity="0.8" />
            <stop offset="60%"  stopColor="#ba55d3" stopOpacity="0.5" />
            <stop offset="80%"  stopColor="#ff0080" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#ff1493" stopOpacity="0" />
          </linearGradient>
        </defs>
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="url(#bp-std-siri-grad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={`${dashLength} ${circumference}`}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
    </span>
  );
};

const ScrollingLoadingText = ({ messages }) => {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (!messages || messages.length <= 1) return undefined;
    const tick = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIdx((p) => (p + 1) % messages.length);
        setFade(true);
      }, 300);
    }, 2500);
    return () => clearInterval(tick);
  }, [messages]);

  return (
    <span className={`bp-std-loading-text ${fade ? 'is-in' : 'is-out'}`}>
      {messages[idx]}
    </span>
  );
};

const StandardLoadingScreen = ({
  isPremium = false,
  message,
  messages,
  showScrollingText = true,
}) => {
  const loadingMessages = messages || DEFAULT_MESSAGES;

  // Toggle a body-level class while this loading screen is mounted so
  // ANY floating CTA / FAB / smart-trigger overlay can globally suppress
  // itself via CSS (`body.bp-loading-active .my-floater { display:none }`)
  // without each one needing its own loading-state plumbing.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.add('bp-loading-active');
    return () => {
      document.body.classList.remove('bp-loading-active');
    };
  }, []);

  return (
    <div
      className={`bp-std-loading ${isPremium ? 'is-premium' : 'is-free'}`}
      data-testid="standard-loading-screen"
    >
      {isPremium && <div className="bp-std-loading-rainbow" aria-hidden="true" />}

      <div className="bp-std-loading-content">
        <SiriStyleSpinner size={70} />
        {message && !messages ? (
          <span className="bp-std-loading-text">{message}</span>
        ) : showScrollingText ? (
          <ScrollingLoadingText messages={loadingMessages} />
        ) : null}
      </div>

      {isPremium && (
        <div className="bp-std-loading-footer">
          Powered by BuildPilot Intelligence
        </div>
      )}
    </div>
  );
};

export default StandardLoadingScreen;
